import { Empty, Flex, Skeleton, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"

import { RoutesPaths } from "../../../../../../constants/routes.ts"
import type { RootState } from "../../../../../../store.ts"
import { serializeData } from "../../../../../../utils/utils"
import { useAlbumsView } from "../../../../../Album/helpers/albums-view-preference.ts"
import {
  albumSortToOrdering,
  sortAlbumsClientSide
} from "../../../../../Album/helpers/sort-albums.ts"
import { albumSetProp } from "../../../../../Album/redux/album.slice.ts"
import {
  deleteAlbum,
  fetchAlbums,
  fetchMoreAlbums,
  updateAlbum
} from "../../../../../Album/redux/album.thunks.ts"
import { type AlbumsFilters } from "../../../../../Album/redux/album.types.ts"
import {
  DEFAULT_ALBUM_SORT_ORDER,
  type AlbumSortOrder
} from "../../../../../Album/types/album-sort.types.ts"
import type { Album, AlbumDeleteFilesMode } from "../../../../../Album/types/album.types.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import { ALBUM_CARD_WITH } from "./album-card-width.ts"
import { DeleteAlbumModal } from "./DeleteAlbum/DeleteAlbumModal.tsx"
import { EditAlbumModal } from "./EditAlbum/EditAlbumModal.tsx"
import { AlbumsListView } from "./ProjectAlbums/AlbumsListView.tsx"
import { ProjectAlbums } from "./ProjectAlbums/ProjectAlbums.tsx"
import { ProjectAlbumsFilters } from "./ProjectAlbums/ProjectAlbumsFilters.tsx"

const { Title, Paragraph } = Typography

interface ProjectViewAlbumsSectionOwnProps {
  onCreateAlbumClick?: () => void
}

function ProjectViewAlbumsSection({
  data,
  fetching,
  projectPermissions,
  getAlbums,
  getMoreAlbums,
  filters,
  setProp,
  userId,
  deleteAlbum,
  updateAlbum,
  onCreateAlbumClick
}: ConnectedProps<typeof connector> & ProjectViewAlbumsSectionOwnProps) {
  const { t } = useTranslation()
  const isInitRef = useRef(true)

  const [deletingAlbumId, setDeletingAlbumId] = useState<string>()
  const [isDeletingAlbum, setIsDeletingAlbum] = useState(false)
  const [sortOrder, setSortOrder] = useState<AlbumSortOrder>(
    DEFAULT_ALBUM_SORT_ORDER
  )
  const [view, setView] = useAlbumsView()

  const isRefetching = !isInitRef?.current

  const navigate = useNavigate()
  const projectId = useParams()?.id as string

  const albums = data?.results || []
  // Only file-count is still ordered here; everything else is asked of the
  // server, which can order the whole list rather than the page that arrived.
  const sortedAlbums = useMemo(
    () => sortAlbumsClientSide(albums, sortOrder),
    [albums, sortOrder]
  )
  const moduleDocs = projectPermissions?.docs
  const createAllowed = moduleDocs?.project_album_create || false
  const hasSearch = !!filters?.q

  const deleteAllowed = !!moduleDocs?.project_doc_delete
  const deleteForeignAllowed =
    deleteAllowed && !!moduleDocs?.project_doc_manage_others
  const editAllowed = !!moduleDocs?.project_doc_edit
  const [editingAlbumId, setEditingAlbumId] = useState<string>()

  const albumPendingDelete = useMemo(
    () => albums.find((a) => a.id === deletingAlbumId),
    [albums, deletingAlbumId]
  )

  const editingAlbum = useMemo(
    () => albums.find((a) => a.id === editingAlbumId),
    [albums, editingAlbumId]
  )

  const handleEditSubmit = useCallback(
    async (data: Partial<Album>) => {
      if (!editingAlbumId) return
      await updateAlbum({ id: editingAlbumId, data }).unwrap()
      setEditingAlbumId(undefined)
    },
    [updateAlbum, editingAlbumId]
  )

  const handleDelete = useCallback(
    async (files: AlbumDeleteFilesMode) => {
      if (!deletingAlbumId) return
      setIsDeletingAlbum(true)
      try {
        // `unwrap` is the point: a rejected delete otherwise settles as a
        // fulfilled promise and the modal closes over an album still there.
        await deleteAlbum({ id: deletingAlbumId, files }).unwrap()
      } catch {
        return
      } finally {
        setIsDeletingAlbum(false)
      }
      setDeletingAlbumId(undefined)
    },
    [deletingAlbumId, deleteAlbum]
  )

  const updateFilters = useCallback(
    (data: Partial<AlbumsFilters>) => {
      setProp({
        key: "filters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleChangeFilter = useCallback(
    (newData: Partial<AlbumsFilters>) => {
      updateFilters({ ...newData, page: 1 })
    },
    [updateFilters]
  )

  // Changing the sort re-asks the server. Page resets with it: page 3 of the
  // old order says nothing about the new one.
  const handleSortChange = useCallback(
    (order: AlbumSortOrder) => {
      setSortOrder(order)
      handleChangeFilter({ ordering: albumSortToOrdering(order) })
    },
    [handleChangeFilter]
  )

  const handleGetAlbums = useCallback(async () => {
    const serializeFilters = serializeData(filters || {})

    await getAlbums({ project: projectId, ...serializeFilters })
    if (isInitRef.current) isInitRef.current = false
  }, [projectId, filters, getAlbums])

  const handleViewAlbum = useCallback(
    (id: string) => {
      navigate(RoutesPaths.project.view(projectId).album(id))
    },
    [projectId, navigate]
  )

  const handleLoadMoreData = useCallback(() => {
    if (!data?.next) return
    const urlObj = new URL(data?.next)
    const params = new URLSearchParams(urlObj.search)
    const paramsObj = Object.fromEntries(params)
    const serializeFilters = serializeData(paramsObj) as AlbumsFilters

    getMoreAlbums({ project: projectId, ...serializeFilters })
  }, [getMoreAlbums, projectId, data?.next])

  useEffect(() => {
    void handleGetAlbums()
  }, [filters, handleGetAlbums])

  if (!isRefetching && fetching) {
    return (
      <Flex
        vertical
        gap={"small"}
        flex={1}
        className={"overflow-hidden w-full"}
      >
        <Skeleton.Node className={"w-full h-[86px]"} />
        <div
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${ALBUM_CARD_WITH}px, 1fr))`
          }}
          className="grid gap-4 overflow-y-auto tailwind-scroll overflow-x-hidden flex-1"
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <Skeleton.Node
              key={i}
              active
              style={{ width: "100%", height: "220px" }}
              className="w-full"
            />
          ))}
        </div>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={"middle"} flex={1} className={"overflow-hidden w-full"}>
      <Flex vertical gap={0}>
        <Title level={5} className="!mb-0">
          {t("files.albums.title")}
        </Title>
        <Paragraph type="secondary" className="!mb-0">
          {t("files.albums.subtitle")}
        </Paragraph>
      </Flex>

      <ProjectAlbumsFilters
        showVisibilityFilter={
          !!projectPermissions?.docs?.project_doc_show_visibility_filter
        }
        filters={filters}
        onFiltersChange={handleChangeFilter}
        sortValue={sortOrder}
        onSortChange={handleSortChange}
        view={view}
        onViewChange={setView}
      />

      {fetching && isRefetching ? (
        <div
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${ALBUM_CARD_WITH}px, 1fr))`
          }}
          className="grid gap-4 overflow-y-auto tailwind-scroll overflow-x-hidden flex-1"
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <Skeleton.Node
              key={i}
              active
              style={{ width: "100%", height: "220px" }}
              className="w-full"
            />
          ))}
        </div>
      ) : hasSearch && !albums.length ? (
        <Flex align="center" justify="center" className="h-[290px]">
          <Empty description={t("files.albums.noSearchResults")} />
        </Flex>
      ) : view === "list" ? (
        <AlbumsListView
          albums={sortedAlbums}
          createAllowed={createAllowed}
          onCreateAlbumClick={onCreateAlbumClick}
          onView={handleViewAlbum}
          userId={userId}
          deleteAllowed={deleteAllowed}
          deleteForeignAllowed={deleteForeignAllowed}
          onDeleteClick={setDeletingAlbumId}
          editAllowed={editAllowed}
          onEditClick={setEditingAlbumId}
        />
      ) : (
        <ProjectAlbums
          albums={sortedAlbums}
          createAllowed={createAllowed}
          onCreateAlbumClick={onCreateAlbumClick}
          hasMore={!!data?.next}
          onLoadMore={handleLoadMoreData}
          onViewAlbum={handleViewAlbum}
          userId={userId}
          deleteAllowed={deleteAllowed}
          deleteForeignAllowed={deleteForeignAllowed}
          onDeleteClick={setDeletingAlbumId}
          editAllowed={editAllowed}
          onEditClick={setEditingAlbumId}
        />
      )}

      {editingAlbum && (
        <EditAlbumModal
          album={editingAlbum}
          onClose={() => setEditingAlbumId(undefined)}
          onSubmit={handleEditSubmit}
        />
      )}

      {albumPendingDelete && (
        <DeleteAlbumModal
          albumName={albumPendingDelete.name}
          fileCount={albumPendingDelete.attachments_count ?? 0}
          isDeleting={isDeletingAlbum}
          onClose={() => setDeletingAlbumId(undefined)}
          onConfirm={(files) => void handleDelete(files)}
        />
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.album.fetching,
  data: state.album.albums,
  filters: state.album.filters,
  projectPermissions: selectProjectWritePermissions(state),
  userId: state.auth.userInfo.id
})

const mapDispatchToProps = {
  getAlbums: fetchAlbums,
  getMoreAlbums: fetchMoreAlbums,
  setProp: albumSetProp,
  deleteAlbum,
  updateAlbum
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewAlbumsSection)
